import React from 'react'
import { urls } from '../src/utils/config'
import { Amplitude, LogOnMount } from '@amplitude/react-amplitude'
import { NextSeo } from 'next-seo'
import { makeStyles, Grid, Typography, Button } from '@material-ui/core'
import { GetStaticProps } from 'next'
import Image from 'next/image'
import { getWallets } from '../src/services/wallets'
import { LoadState } from '../types/general'
import { Wallet as IWallet, WalletType } from '../types/wallet'
import WalletGrid from '../src/components/Product/wallets/WalletGrid'
import Nav from '../src/components/Nav'
import ProductNav from '../src/components/Product/Nav'
import WalletGuide from '../src/components/Product/wallets/WalletGuide'
import FAQSection from '../src/components/Product/FAQSection'
import QuickLinks from '../src/components/Footer/QuickLinks'
import Footer from '../src/components/Footer'

const useStyles = makeStyles((theme) => ({
  pageWrapper: {
    background: 'white',
  },
  heroSection: {
    background: 'linear-gradient(180deg, rgba(209, 197, 224, 0.2) 0.13%, #FFFFFF 100%)',
    overflow: 'auto',
  },
  section: {
    padding: '30px 20px',
    margin: '4rem auto',
    [theme.breakpoints.up('md')]: {
      padding: '30px 80px',
    },
    [theme.breakpoints.up('xl')]: {
      padding: '30px 0',
      maxWidth: '1200px',
    },
    '&>div': {
      padding: '0 20px',
    },
    '& h1': {
      [theme.breakpoints.down('sm')]: {
        fontSize: '54px',
      },
    },
    '& h1, & h3': {
      letterSpacing: '-0.055em',
      margin: '1rem 0',
    },
    '& p': {
      fontSize: '16px',
      color: '#7D7787',
    },
  },
  heroContentSection: {},
  heroCTA: {
    textTransform: 'unset',
    margin: '1rem 0',
  },
  videoWrapper: {
    position: 'relative',
    height: '360px',
    overflow: 'hidden',
    borderRadius: '1rem',
    '& iframe': {
      position: 'absolute',
      width: '100%',
      height: '100%',
      top: 0,
      left: 0,
    },
  },
  listWrapper: {
    width: '100%',
    [theme.breakpoints.up('md')]: {
      width: '1020px',
      margin: 'auto',
    },
  },
}))

const faqs = [
  {
    question: 'How to choose a good hardware wallet?',
    answer:
      'It’s important to buy a hardware wallet that is easily usable. That includes having a big screen from which you can read information easily and buttons to confirm actions.',
  },
  {
    question: 'How to assess the security of a hardware wallet?',
    answer:
      'All hardware wallets are optimized for security and yet most of them have been exploited (mostly by security experts in labs) in the past. \n It’s important to understand the [different attack vectors](https://shiftcrypto.ch/bitbox02/threat-model/) and read about what manufacturers do to protect against them. One of the most important criteria for a hardware wallet is that all its components are open-source so that security experts can scrutinize it.',
  },
  {
    question: 'Can I use my hardware wallet with any software wallet?',
    answer:
      'It’s important that the hardware wallet is well integrated with a broad range of wallets. If you buy a hardware wallet that’s not supported by most software wallets you will be limited to the wallet app of the vendor. Currently the hardware wallets that offer most integrations are Ledger, Lattice 1 and Keystore.',
  },
]

interface Props {
  wallets: IWallet[]
  initialLoadState: LoadState
}

const Wallets = ({ wallets }: Props): JSX.Element => {
  const classes = useStyles({})
  return (
    <Amplitude
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      eventProperties={(inheritedProps: { page: any }) => ({
        ...inheritedProps,
        page: {
          ...inheritedProps.page,
          name: urls.hardwareWallets,
        },
      })}
    >
      {(): JSX.Element => (
        <>
          <LogOnMount eventType="page-displayed" />
          <NextSeo
            title={`Cryptotesters.com - Wallets`}
            description={`Cryptotesters.com.`}
            openGraph={{
              url: `https://cryptotesters.com`,
              title: `Cryptotesters.com - Wallets`,
              description: `Wallets Cryptotesters.com.`,
              images: [
                {
                  url: `https://${process.env.NEXT_PUBLIC_APP_URL}/og/pages/large/home.jpg`,
                  width: 1200,
                  height: 630,
                  alt: `Cryptotesters.com`,
                },
                {
                  url: `https://${process.env.NEXT_PUBLIC_APP_URL}/og/pages/small/home.jpg`,
                  width: 600,
                  height: 400,
                  alt: `Cryptotesters.com Logo`,
                },
              ],
              site_name: 'Cryptotesters.com',
            }}
            twitter={{
              handle: '@cryptotesters',
              site: '@cryptotesters',
              cardType: 'summary_large_image',
            }}
          />
          <div className={classes.pageWrapper}>
            <div className={classes.heroSection}>
              <Nav dark />
              <ProductNav />

              <Grid container className={classes.section}>
                <Grid item md={5} xs={12}>
                  <Typography variant="h1">The Best Hardware wallets</Typography>
                  <Typography variant="body2">
                    Hardware wallets are specialized physical devices that safely store private keys
                    and the associated cryptocurrencies of users. Hardware wallets are used in
                    combination with software apps - mobile or desktop - that let you transfer your
                    funds. The Hardware wallet holds the key to authorize transfers.
                  </Typography>
                  <Button
                    color="primary"
                    variant="contained"
                    href="#wallets"
                    className={classes.heroCTA}
                  >
                    Learn more about wallets
                  </Button>
                </Grid>

                <Grid item md={7} xs={12}>
                  <div>
                    <div className={classes.videoWrapper}>
                      <Image src="/key.png" objectFit="contain" layout="fill" />
                    </div>
                  </div>
                </Grid>
              </Grid>
              <div className={`${classes.section} ${classes.listWrapper}`} id="wallets">
                <Typography align="center" variant="h3">
                  Compare Hardware wallets
                </Typography>

                <WalletGrid wallets={wallets} type={WalletType.physical} />
              </div>
            </div>

            <div>
              <WalletGuide />
              <FAQSection faqs={faqs} />
            </div>
            <QuickLinks />
            <Footer />
          </div>
        </>
      )}
    </Amplitude>
  )
}

export const getStaticProps: GetStaticProps = async () => {
  let loadState = LoadState.LOADING
  let wallets: IWallet[] = []

  try {
    wallets = await getWallets('hardware-wallets')
    loadState = LoadState.LOADED
  } catch (e) {
    loadState = LoadState.ERROR
  }

  return {
    props: {
      initialLoadState: loadState,
      wallets,
    },
  }
}

export default Wallets
